import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import { contractService } from '../../services/contractService';

interface Contract {
    id: number;
    number: string;
    status: 'DRAFT' | 'PENDING_SIGNATURE' | 'SIGNED' | 'SIGNED_BY_BUYER' | 'SIGNED_BY_SUPPLIER' | 'SIGNED_BY_BOTH' | 'CANCELLED' | 'EXPIRED' | 'APPROVED' | 'PENDING_MODERATION' | 'NEEDS_REVISION';
    filePath?: string;
    cmsZipFilePath?: string;
    signedAt?: string;
    createdAt: string;
    updatedAt?: string;
    company?: {
        id: number;
        bin: string;
        name: string;
    };
    signatory?: {
        id: number;
        phoneNumber: string;
    };
}


export const ContractsPage: React.FC = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<'all' | 'active' | 'pending' | 'completed'>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
    const [showContractModal, setShowContractModal] = useState(false);
    const [contracts, setContracts] = useState<Contract[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Загрузка договоров при монтировании компонента
    useEffect(() => {
        loadContracts();
    }, []);

    const loadContracts = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await api.contracts.getMyContracts();
            if (response.success && response.data) {
                setContracts(response.data);
            } else {
                setError('Не удалось загрузить договоры');
            }
        } catch (err) {
            console.error('Ошибка загрузки договоров:', err);
            setError('Ошибка загрузки договоров');
        } finally {
            setLoading(false);
        }
    };

    const handleAutoGenerateContract = async () => {
        try {
            setLoading(true);
            const response = await api.contracts.autoGenerate();
            if (response.success) {
                // Перезагружаем список договоров
                await loadContracts();
            } else {
                setError('Не удалось сгенерировать договор');
            }
        } catch (err) {
            console.error('Ошибка генерации договора:', err);
            setError('Ошибка генерации договора');
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadContract = async (contractId: number) => {
        try {
            const response = await api.contracts.download(contractId);
            if (response.success && response.url) {
                // Создаем ссылку для скачивания
                const link = document.createElement('a');
                link.href = response.url;
                link.download = `contract-${contractId}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (err) {
            console.error('Ошибка скачивания договора:', err);
            setError('Ошибка скачивания договора');
        }
    };

    const handleDownloadCmsZip = async (contractId: number, contractNumber: string) => {
        try {
            await contractService.downloadCmsZipFile(contractId, contractNumber);
        } catch (err) {
            console.error('Ошибка скачивания CMS архива:', err);
            setError('Ошибка скачивания CMS архива');
        }
    };


    const getStatusColor = (status: Contract['status']) => {
        const statusColors = {
            DRAFT: 'bg-gray-100 text-gray-700 border-gray-300',
            PENDING_SIGNATURE: 'bg-blue-100 text-blue-700 border-blue-300',
            SIGNED_BY_BUYER: 'bg-blue-100 text-blue-700 border-blue-300',
            SIGNED_BY_SUPPLIER: 'bg-indigo-100 text-indigo-700 border-indigo-300',
            SIGNED_BY_BOTH: 'bg-green-100 text-green-700 border-green-300',
            SIGNED: 'bg-green-100 text-green-700 border-green-300',
            CANCELLED: 'bg-red-100 text-red-700 border-red-300',
            EXPIRED: 'bg-gray-100 text-gray-700 border-gray-300',
            APPROVED: 'bg-green-100 text-green-700 border-green-300',
            PENDING_MODERATION: 'bg-yellow-100 text-yellow-700 border-yellow-300',
            NEEDS_REVISION: 'bg-orange-100 text-orange-700 border-orange-300'
        };
        return statusColors[status] || 'bg-gray-100 text-gray-700 border-gray-300';
    };

    const getStatusText = (status: Contract['status']) => {
        const statusTexts = {
            DRAFT: 'Черновик',
            PENDING_SIGNATURE: 'Ожидает подписания',
            SIGNED_BY_BUYER: 'Подписан покупателем',
            SIGNED_BY_SUPPLIER: 'Подписан поставщиком',
            SIGNED_BY_BOTH: 'Подписан обеими сторонами',
            SIGNED: 'Подписан',
            CANCELLED: 'Отменен',
            EXPIRED: 'Истек срок действия',
            APPROVED: 'Одобрен',
            PENDING_MODERATION: 'На модерации',
            NEEDS_REVISION: 'Требует доработки'
        };
        return statusTexts[status] || status;
    };


    const filteredContracts = contracts.filter(contract => {
        const matchesSearch = contract.number.toLowerCase().includes(searchTerm.toLowerCase());

        if (activeTab === 'all') return matchesSearch;
        if (activeTab === 'active') return matchesSearch && contract.status === 'SIGNED';
        if (activeTab === 'pending') return matchesSearch && contract.status === 'PENDING_SIGNATURE';
        if (activeTab === 'completed') return matchesSearch && ['CANCELLED', 'EXPIRED'].includes(contract.status);

        return matchesSearch;
    });

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const openContractModal = (contract: Contract) => {
        setSelectedContract(contract);
        setShowContractModal(true);
    };

    const closeContractModal = () => {
        setSelectedContract(null);
        setShowContractModal(false);
    };

    const handleSignContract = (contractId: number) => {
        // Переходим на страницу подписания договора
        navigate(`/contract-signing/${contractId}`);
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Договоры</h1>
                    <p className="text-gray-600 mt-1">Управление договорами и соглашениями с компанией</p>
                </div>
                <button
                    onClick={handleAutoGenerateContract}
                    disabled={loading}
                    className="bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-3 rounded-xl font-medium hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    {loading ? 'Генерация...' : 'Сгенерировать договор'}
                </button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {[
                    { label: 'Всего договоров', value: contracts.length, color: 'blue' },
                    { label: 'Подписанные', value: contracts.filter(c => c.status === 'SIGNED').length, color: 'green' },
                    { label: 'Ожидают подписания', value: contracts.filter(c => c.status === 'PENDING_SIGNATURE').length, color: 'orange' },
                    { label: 'Черновики', value: contracts.filter(c => c.status === 'DRAFT').length, color: 'purple' }
                ].map((stat, index) => (
                    <div key={index} className={`bg-gradient-to-r ${stat.color === 'blue' ? 'from-blue-50 to-blue-100 border-blue-200' :
                        stat.color === 'green' ? 'from-green-50 to-green-100 border-green-200' :
                            stat.color === 'orange' ? 'from-orange-50 to-orange-100 border-orange-200' :
                                'from-purple-50 to-purple-100 border-purple-200'
                        } border rounded-xl p-6`}>
                        <h3 className="text-sm font-medium text-gray-600">{stat.label}</h3>
                        <p className={`text-2xl font-bold mt-2 ${stat.color === 'blue' ? 'text-blue-600' :
                            stat.color === 'green' ? 'text-green-600' :
                                stat.color === 'orange' ? 'text-orange-600' :
                                    'text-purple-600'
                            }`}>{stat.value}</p>
                    </div>
                ))}
            </div>

            {/* Filters and Search */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    {/* Tabs */}
                    <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
                        {[
                            { key: 'all', label: 'Все договоры' },
                            { key: 'active', label: 'Активные' },
                            { key: 'pending', label: 'В процессе' },
                            { key: 'completed', label: 'Завершенные' }
                        ].map((tab) => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key as any)}
                                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === tab.key
                                    ? 'bg-white text-gray-900 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-900'
                                    }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Search */}
                    <div className="relative">
                        <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                            type="text"
                            placeholder="Поиск по номеру или названию..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full sm:w-80"
                        />
                    </div>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <div className="flex items-center">
                        <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-red-700 font-medium">{error}</p>
                        <button
                            onClick={() => setError(null)}
                            className="ml-auto text-red-400 hover:text-red-600"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>
            )}

            {/* Loading State */}
            {loading && (
                <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-blue-600 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Загрузка договоров...</h3>
                    <p className="text-gray-600">Пожалуйста, подождите</p>
                </div>
            )}

            {/* Contracts List */}
            {!loading && (
                <div className="space-y-4">
                    {filteredContracts.length === 0 ? (
                        <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">Договоры не найдены</h3>
                            <p className="text-gray-600 mb-6">Попробуйте изменить параметры поиска или создайте новый договор</p>
                            <button
                                onClick={handleAutoGenerateContract}
                                disabled={loading}
                                className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? 'Генерация...' : 'Сгенерировать первый договор'}
                            </button>
                        </div>
                    ) : (
                        filteredContracts.map((contract) => (
                            <div key={contract.id} className="bg-white rounded-2xl border border-gray-200 hover:shadow-lg transition-all duration-200 overflow-hidden">
                                <div className="p-6">
                                    {/* Contract Header */}
                                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
                                        <div className="flex items-start space-x-4">
                                            <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-green-200 rounded-xl flex items-center justify-center flex-shrink-0">
                                                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-3 mb-2">
                                                    <h3 className="text-lg font-semibold text-gray-900">{contract.number}</h3>
                                                </div>
                                                <h4 className="text-md font-medium text-gray-800 mb-2">Договор поставки продукции птицеводства</h4>
                                                <p className="text-sm text-gray-600 mb-3">Договор на поставку свежего куриного мяса различных категорий</p>
                                                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                                                    <span>Создан: {formatDate(contract.createdAt)}</span>
                                                    {contract.signedAt && (
                                                        <span>Подписан: {formatDate(contract.signedAt)}</span>
                                                    )}
                                                    {contract.company && (
                                                        <span>Компания: {contract.company.name}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex flex-col items-end gap-3">
                                            <span className={`px-3 py-1 text-sm font-medium rounded-full border ${getStatusColor(contract.status)}`}>
                                                {getStatusText(contract.status)}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Contract Info */}
                                    <div className="bg-gray-50 rounded-xl p-4 mb-4">
                                        <h5 className="text-sm font-medium text-gray-900 mb-3">Информация о договоре:</h5>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Статус:</span>
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(contract.status)}`}>
                                                    {getStatusText(contract.status)}
                                                </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Компания:</span>
                                                <span className="font-medium">{contract.company?.name || 'Не указана'}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">БИН:</span>
                                                <span className="font-medium">{contract.company?.bin || 'Не указан'}</span>
                                            </div>
                                            {contract.signedAt && (
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Подписан:</span>
                                                    <span className="font-medium">{formatDate(contract.signedAt)}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex flex-col sm:flex-row gap-3">
                                        <button
                                            onClick={() => openContractModal(contract)}
                                            className="flex-1 bg-blue-50 text-blue-700 px-4 py-2 rounded-lg hover:bg-blue-100 transition-colors font-medium flex items-center justify-center"
                                        >
                                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                            Просмотреть
                                        </button>

                                        {contract.status === 'PENDING_SIGNATURE' && (
                                            <button
                                                onClick={() => handleSignContract(contract.id)}
                                                className="flex-1 bg-green-50 text-green-700 px-4 py-2 rounded-lg hover:bg-green-100 transition-colors font-medium flex items-center justify-center"
                                            >
                                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                                Подписать
                                            </button>
                                        )}

                                        <button
                                            onClick={() => handleDownloadContract(contract.id)}
                                            className="flex-1 bg-gray-50 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors font-medium flex items-center justify-center"
                                        >
                                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                            Скачать PDF
                                        </button>

                                        {contract.cmsZipFilePath && contract.status === 'SIGNED_BY_BOTH' && (
                                            <button
                                                onClick={() => handleDownloadCmsZip(contract.id, contract.number)}
                                                className="flex-1 bg-green-50 text-green-700 px-4 py-2 rounded-lg hover:bg-green-100 transition-colors font-medium flex items-center justify-center"
                                            >
                                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                                Скачать CMS (ZIP)
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* Contract Details Modal */}
            {showContractModal && selectedContract && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">{selectedContract.number}</h2>
                                <p className="text-gray-600">Договор поставки продукции птицеводства</p>
                            </div>
                            <button
                                onClick={closeContractModal}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                            <div className="space-y-6">
                                {/* Contract Info */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <h3 className="text-sm font-medium text-gray-900 mb-3">Основная информация</h3>
                                        <div className="space-y-2 text-sm">
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Статус:</span>
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedContract.status)}`}>
                                                    {getStatusText(selectedContract.status)}
                                                </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Создан:</span>
                                                <span className="font-medium">{formatDate(selectedContract.createdAt)}</span>
                                            </div>
                                            {selectedContract.signedAt && (
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Подписан:</span>
                                                    <span className="font-medium">{formatDate(selectedContract.signedAt)}</span>
                                                </div>
                                            )}
                                            {selectedContract.company && (
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Компания:</span>
                                                    <span className="font-medium">{selectedContract.company.name}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>

                        {/* Кнопки действий в модальном окне */}
                        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
                            <button
                                onClick={closeContractModal}
                                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                            >
                                Закрыть
                            </button>

                            {selectedContract.status === 'PENDING_SIGNATURE' && (
                                <button
                                    onClick={() => {
                                        closeContractModal();
                                        handleSignContract(selectedContract.id);
                                    }}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center"
                                >
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                    Подписать договор
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
